import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getSupabase } from "@/lib/hooks/api/utils";

export function useBulkApproveTimeEntries() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryIds: string[]) => {
      const supabase = getSupabase();

      if (!entryIds || !Array.isArray(entryIds) || entryIds.length === 0) {
        throw new Error("Time entry IDs array is required");
      }

      const { data: existingEntries, error: fetchError } = await supabase
        .from("time_entries")
        .select(
          `
                    id,
                    is_approved,
                    sync_status
                `
        )
        .in("id", entryIds);

      if (fetchError) {
        console.error("Error fetching time entries:", fetchError);
        throw new Error("Failed to fetch time entries");
      }

      if (!existingEntries || existingEntries.length === 0) {
        throw new Error("No valid time entries found");
      }

      const unapprovedEntries = existingEntries.filter((entry) => !entry.is_approved);
      const alreadyApprovedCount = existingEntries.length - unapprovedEntries.length;

      if (unapprovedEntries.length === 0) {
        throw new Error("All selected time entries are already approved");
      }

      const user = (await supabase.auth.getUser()).data.user;
      const updatePromises = unapprovedEntries.map((entry) => {
        const updateData: {
          is_approved: boolean;
          approved_by: string | null;
          approved_at: string;
          updated_at: string;
          sync_status?: string;
        } = {
          is_approved: true,
          approved_by: user?.id || null,
          approved_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        if (entry.sync_status === "disapproved") {
          updateData.sync_status = "pending";
        }

        return supabase.from("time_entries").update(updateData).eq("id", entry.id).select("id");
      });

      const updateResults = await Promise.all(updatePromises);
      const updateError = updateResults.find((result) => result.error)?.error;

      if (updateError) {
        console.error("Error bulk approving time entries:", updateError);
        throw new Error("Failed to approve time entries");
      }

      const updatedEntries = updateResults
        .filter((result) => result.data && result.data.length > 0)
        .flatMap((result) => result.data || []);

      return {
        success: true,
        approvedCount: updatedEntries?.length || 0,
        alreadyApprovedCount,
        totalProcessed: existingEntries.length,
      };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["time-entries"] });
    },
  });
}

export function useUpdateTimeEntrySyncStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      syncStatus,
      quickBooksId,
      syncedAt,
    }: {
      id: string;
      syncStatus: string;
      quickBooksId?: string;
      syncedAt?: string;
    }) => {
      const supabase = getSupabase();

      if (!syncStatus) {
        throw new Error("syncStatus is required");
      }

      const validStatuses = ["pending", "synced", "failed", "disapproved"];
      if (!validStatuses.includes(syncStatus)) {
        throw new Error("Invalid sync status");
      }

      const { data, error } = await supabase
        .from("time_entries")
        .update({
          sync_status: syncStatus,
          quickbooks_id: quickBooksId || null,
          synced_at: syncedAt || new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("Database error:", error);
        throw new Error("Failed to update time entry sync status");
      }

      return data;
    },
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ["time-entry", id] });
      void queryClient.invalidateQueries({ queryKey: ["time-entries"] });
    },
  });
}

export function useBulkRejectTimeEntries() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryIds: string[]) => {
      const supabase = getSupabase();

      if (!entryIds || !Array.isArray(entryIds) || entryIds.length === 0) {
        throw new Error("Time entry IDs array is required");
      }

      const { data, error } = await supabase
        .from("time_entries")
        .update({
          is_approved: false,
          approved_by: null,
          approved_at: null,
          updated_at: new Date().toISOString(),
        })
        .in("id", entryIds)
        .select("id");

      if (error) {
        console.error("Error bulk rejecting time entries:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["time-entries"] });
    },
    onError: (error) => {
      console.error("Error bulk rejecting time entries:", error);
    },
  });
}

export function useBulkDeleteTimeEntries() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryIds: string[]) => {
      const supabase = getSupabase();

      if (!entryIds || !Array.isArray(entryIds) || entryIds.length === 0) {
        throw new Error("Time entry IDs array is required");
      }

      const { data, error } = await supabase
        .from("time_entries")
        .delete()
        .in("id", entryIds)
        .select("id");

      if (error) {
        console.error("Error bulk deleting time entries:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["time-entries"] });
    },
    onError: (error) => {
      console.error("Error bulk deleting time entries:", error);
    },
  });
}
